/**
 * P-57b (D-232 sec.2 / QX-601) - pure helpers behind SelectLayer, the designed
 * list that replaces the browser's own `<select>` popup everywhere. No DOM here.
 */
export interface SelectItem {
  /** Index in the native select's `options` (what gets written back). */
  index: number;
  label: string;
  disabled: boolean;
  /** Label of the enclosing `<optgroup>`, shown once above its first item. */
  group?: string;
}

/** The next enabled item from `from` in direction `dir` (+1 / -1); stays put at the ends. */
export function stepIndex(items: SelectItem[], from: number, dir: 1 | -1): number {
  for (let i = from + dir; i >= 0 && i < items.length; i += dir) if (!items[i].disabled) return i;
  return from;
}

export function firstEnabled(items: SelectItem[]): number {
  const i = items.findIndex((x) => !x.disabled);
  return i < 0 ? 0 : i;
}

export function lastEnabled(items: SelectItem[]): number {
  for (let i = items.length - 1; i >= 0; i--) if (!items[i].disabled) return i;
  return 0;
}

/** Type-ahead: the first enabled item after `from` (wrapping) whose label starts with `buffer`. */
export function typeahead(items: SelectItem[], buffer: string, from: number): number {
  const q = buffer.trim().toLocaleLowerCase();
  if (!q) return -1;
  // A repeated single letter cycles through the matches; a longer buffer searches from the current item.
  const start = buffer.length > 1 ? from : from + 1;
  for (let k = 0; k < items.length; k++) {
    const i = (start + k + items.length) % items.length;
    if (!items[i].disabled && items[i].label.toLocaleLowerCase().startsWith(q)) return i;
  }
  return -1;
}

/** A select we leave to the browser: multi-select, a listbox (size > 1) or an explicit opt-out. */
export function isNativeOnly(el: { multiple: boolean; size: number; hasAttribute(n: string): boolean }): boolean {
  return el.multiple || el.size > 1 || el.hasAttribute('data-native-select');
}
